import { roleCounterpart, useClientRole } from "@/components/client-role";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import { Bell, Eye, LogOut, Moon, ShieldCheck, Sun } from "lucide-react";
import { DropdownMenu as DM } from "radix-ui";

// There's no auth yet, so the signed-in user is mocked per role with seed-consistent identities: an OW
// staffer in admin, a client user in viewer. This menu is the home for PERSONAL PREFERENCES — appearance,
// notifications, sign out — so they live under the account rather than cluttering the bar (the bar is
// navigation + search). When auth lands, `USERS[role]` is the only thing that gets replaced by the session.
const USERS = {
  admin: { name: "Alex Morgan", email: "alex.morgan@oliverwyman.com", role: "Admin" },
  viewer: { name: "Jordan Lee", email: "jordan.lee@client.com", role: "Viewer" },
} as const;

function initials(name: string) {
  const p = name.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

const MENU_ROW =
  "flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-accent";

// The two in-menu segmented controls — View as + Appearance — share ONE look, and the SELECTED cell uses the
// same solid inverted pill as the bar's active tab and the filter chips (one "selected" language across the
// whole app). A raised `bg-card` cell was the obvious choice but it's invisible in dark — every surface token
// sits ~1.06:1 from the `bg-muted` track — so the fill has to be foreground-based to register. `SEG` is the
// track; `segItem` is a cell. Defining them once keeps the two controls identical.
const SEG = "inline-flex w-full rounded-lg bg-muted p-0.5";
const segItem = (active: boolean) =>
  cn(
    "inline-flex flex-1 items-center justify-center gap-1.5 rounded-md py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
    active ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:text-foreground",
  );

/** View-as: hop between the two role trees (viewer "/" ↔ admin "/admin-view") at the equivalent page. These
 *  are Links, not buttons — choosing a role is a navigation that changes the URL (and, with real auth, this
 *  control wouldn't exist: role would come from the session). Same segmented pill as Appearance, as asked. */
function ViewAsToggle() {
  const role = useClientRole();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const opts = [
    { value: "viewer", label: "Viewer", icon: Eye },
    { value: "admin", label: "Admin", icon: ShieldCheck },
  ] as const;
  return (
    <div className={SEG}>
      {opts.map(({ value, label, icon: Icon }) => (
        <Link
          key={value}
          to={roleCounterpart(pathname, value) as never}
          aria-current={role === value ? "true" : undefined}
          className={segItem(role === value)}
        >
          <Icon className="size-3.5" /> {label}
        </Link>
      ))}
    </div>
  );
}

/** Appearance: Light/Dark. Plain buttons (not menu items) so a click flips the theme WITHOUT closing the
 *  menu — the change lands behind the open popup. */
function AppearanceToggle() {
  const { theme, toggle } = useTheme();
  const opts = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
  ] as const;
  return (
    <div className={SEG}>
      {opts.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={theme === value}
          onClick={() => {
            if (theme !== value) toggle();
          }}
          className={segItem(theme === value)}
        >
          <Icon className="size-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

/** The account avatar + its menu — the far-right anchor of the utility zone. Holds who you are and the
 *  personal settings a portal like this needs (appearance, notifications, sign out); admin-scoped or
 *  billing-type settings would nest here too as they arrive. */
export function AccountMenu() {
  const role = useClientRole();
  const u = USERS[role];
  return (
    <DM.Root>
      <DM.Trigger asChild>
        <button
          type="button"
          aria-label="Account and settings"
          className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground outline-none transition-all hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-ring"
        >
          {initials(u.name)}
        </button>
      </DM.Trigger>
      <DM.Portal>
        <DM.Content
          align="end"
          sideOffset={8}
          className="z-50 w-64 rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-lg outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
              {initials(u.name)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{u.name}</span>
                <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {u.role}
                </span>
              </div>
              <div className="truncate text-xs text-muted-foreground">{u.email}</div>
            </div>
          </div>

          <DM.Separator className="my-1 h-px bg-border" />

          <div className="px-2 py-1.5">
            <div className="field-label mb-1.5">View as</div>
            <ViewAsToggle />
          </div>
          <div className="px-2 py-1.5">
            <div className="field-label mb-1.5">Appearance</div>
            <AppearanceToggle />
          </div>

          <DM.Separator className="my-1 h-px bg-border" />

          <DM.Item className={MENU_ROW}>
            <Bell className="size-4 shrink-0 text-muted-foreground" /> Notification settings
          </DM.Item>
          <DM.Item className={MENU_ROW}>
            <LogOut className="size-4 shrink-0 text-muted-foreground" /> Sign out
          </DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
